import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { API_URL } from "../../utils/config";

const PAGE_SIZES = [10, 25, 50];
const TIPUS_ORDERS = [
  ["Empresa", "Autonom", "Particular"],
  ["Autonom", "Particular", "Empresa"],
  ["Particular", "Empresa", "Autonom"],
];

function idProveidor(p) {
  return p ? Number(p.idProveidors) : null;
}

function nomComplet(proveidor) {
  return [
    [proveidor.Cognom1, proveidor.Cognom2].filter((x) => x && String(x).trim()).join(" "),
    proveidor.Nom,
  ]
    .filter((x) => x && String(x).trim())
    .join(", ");
}

function telefonPrincipal(proveidor) {
  return proveidor.TelefonFixe || proveidor.TelefonMobil || "";
}

function SeleccionarProveidor({
  onProveidorSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  onContactes,
  onEmpleats,
  onSucursals,
  selectedProveidor,
  filterText,
  setFilterText,
  setProveidors,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [proveidors, setProveidorsState] = useState([]);
  const [filteredProveidors, setFilteredProveidors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchProveidors = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Proveidors.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setProveidorsState(result.success ? result.proveidors || [] : []);
      } catch (err) {
        console.error("Error carregant proveïdors:", err);
        setProveidorsState([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProveidors();
  }, [empresaSeleccionada, reloadToken]);

  const handleSort = (key) => {
    if (key === "TipusEntitat") {
      const currentIndex = TIPUS_ORDERS.findIndex(
        (order) => JSON.stringify(order) === JSON.stringify(sortConfig.tipusOrder)
      );
      const nextTipusOrder = TIPUS_ORDERS[(currentIndex + 1) % TIPUS_ORDERS.length];
      setSortConfig({ key, direction: "asc", tipusOrder: nextTipusOrder });
      return;
    }
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction, tipusOrder: sortConfig.tipusOrder });
  };

  useEffect(() => {
    const t = (filterText || "").toLowerCase();
    const filtered = (proveidors || []).filter((proveidor) => {
      const camp = [
        proveidor.Nom,
        proveidor.Cognom1,
        proveidor.Cognom2,
        proveidor.NomVia,
        proveidor.Poblacio,
        proveidor.TelefonFixe,
        proveidor.TelefonMobil,
        proveidor.TelefonMobil2,
        proveidor.Whatsapp,
        proveidor.Email1,
        proveidor.Email2,
        proveidor.CIF_DNI,
        proveidor.NomComercial,
        proveidor.ProfessioDescripcio,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !t || camp.includes(t);
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue;
        let bValue;
        if (sortConfig.key === "TipusEntitat") {
          const tipusOrder = sortConfig.tipusOrder || TIPUS_ORDERS[0];
          aValue = tipusOrder.indexOf(a.TipusEntitat);
          bValue = tipusOrder.indexOf(b.TipusEntitat);
        } else if (sortConfig.key === "Nom") {
          aValue = nomComplet(a).toLowerCase();
          bValue = nomComplet(b).toLowerCase();
        } else if (sortConfig.key === "Telefon") {
          aValue = telefonPrincipal(a).toString();
          bValue = telefonPrincipal(b).toString();
        } else {
          aValue = (a[sortConfig.key] || "").toString().toLowerCase();
          bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredProveidors(filtered);
    setProveidors?.(filtered);
    setPage(1);
  }, [filterText, proveidors, sortConfig, setProveidors]);

  useEffect(() => {
    if (!selectedProveidor) return;
    const stillVisible = filteredProveidors.some((p) => idProveidor(p) === idProveidor(selectedProveidor));
    if (!stillVisible) onProveidorSelect?.(null);
  }, [filteredProveidors, selectedProveidor, onProveidorSelect]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, proveidor) => {
    e.stopPropagation();
    const id = idProveidor(proveidor);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 280;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredProveidors.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredProveidors.slice(inici, inici + pageSize);
  const proveidorMenu = menuObert ? filteredProveidors.find((p) => idProveidor(p) === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (proveidor) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idProveidor(proveidor) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, proveidor)}
      >
        …
      </button>
    </div>
  );

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom, cognoms, adreça, telèfon o e-mail…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant proveïdors…</p>
      ) : filteredProveidors.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap proveïdor.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((proveidor) => (
                <article
                  key={idProveidor(proveidor)}
                  className="dv2-record"
                  onClick={() => onProveidorSelect?.(proveidor)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(proveidor)}
                >
                  <div>
                    <p className="dv2-record-title">{nomComplet(proveidor) || "—"}</p>
                    <p className="dv2-record-meta">
                      {[
                        mapToPresentable(proveidor.TipusEntitat, mapejos.tipusEntitat),
                        proveidor.Poblacio,
                        telefonPrincipal(proveidor),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {renderAccions(proveidor)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("TipusEntitat", "Organització")}
                    {th("CIF_DNI", "CIF/NIF")}
                    {th("Nom", "Nom")}
                    {th("NomComercial", "Nom comercial")}
                    {th("Telefon", "Telèfon")}
                    {th("Email1", "E-mail")}
                    {th("Poblacio", "Població")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((proveidor) => {
                    const tipus = mapToPresentable(proveidor.TipusEntitat, mapejos.tipusEntitat);
                    return (
                      <tr
                        key={idProveidor(proveidor)}
                        className={idProveidor(selectedProveidor) === idProveidor(proveidor) ? "is-selected" : ""}
                        onClick={() => onProveidorSelect?.(proveidor)}
                        onDoubleClick={() => pucConsultar && onConsultar?.(proveidor)}
                      >
                        <td>{tipus || "—"}</td>
                        <td>
                          {tipus === "Empresa" ? "CIF" : "NIF"}: {proveidor.CIF_DNI || "—"}
                        </td>
                        <td>{nomComplet(proveidor) || "—"}</td>
                        <td>{proveidor.NomComercial || "—"}</td>
                        <td>{telefonPrincipal(proveidor) || "—"}</td>
                        <td>{proveidor.Email1 || "—"}</td>
                        <td>{proveidor.Poblacio || "—"}</td>
                        <td className="dv2-table-actions">{renderAccions(proveidor)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {proveidorMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onConsultar?.(proveidorMenu))}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onModificar?.(proveidorMenu))}>
                Modificar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onContactes?.(proveidorMenu))}>
                Contactes
              </button>
            )}
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onEmpleats?.(proveidorMenu))}>
                Empleats
              </button>
            )}
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onSucursals?.(proveidorMenu))}>
                Sucursals
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => tancarI(() => onEsborrar?.(proveidorMenu))}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarProveidor;
